import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useInfiniteList } from "@hooks";
import {
  assignCategoryToSizes,
  createUnitSize,
  deleteUnitSize,
  getAllUnitSizes,
  getSizesByCategory,
  updateUnitSize,
  uploadUnitSizeImage,
} from "@/api/queries/UnitSizes";
import {
  AssignCategoryPayload,
  UnitSize,
  UnitSizePayload,
  UnitSizeRead,
} from "@types";

export const UNIT_SIZES_QK = ["sizes"] as const;

export function useAllUnitSizes(withPagination: boolean = true) {
  return useInfiniteList<UnitSizeRead>({
    queryKey: [...UNIT_SIZES_QK, withPagination],
    fetchFn: async (page: number) => {
      const res = await getAllUnitSizes(
        withPagination ? page : undefined,
        withPagination,
      );
      return res.data;
    },
  });
}

export function useSizesByCategory(
  unitCategoryId: string | null,
  limit?: number,
  withPagination: boolean = true,
) {
  return useInfiniteList<UnitSizeRead>({
    queryKey: ["sizesByCategory", unitCategoryId, limit, withPagination],
    fetchFn: async (page: number) => {
      if (!unitCategoryId) {
        return { data: [], totalCount: 0, totalPages: 1 };
      }
      const res = await getSizesByCategory(
        unitCategoryId,
        withPagination ? page : undefined,
        limit,
      );
      return res.data;
    },
  });
}

export function useCreateUnitSizes() {
  const queryClient = useQueryClient();

  const { mutateAsync: triggerCreateUnitSize, isPending: loading } =
    useMutation<UnitSizeRead, Error, UnitSize>({
      mutationFn: createUnitSize,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_SIZES_QK });
      },
    });

  return { triggerCreateUnitSize, loading };
}

export function useUpdateUnitSizes() {
  const queryClient = useQueryClient();

  type UpdateArgs = { id: string; payload: UnitSizePayload };

  const { mutateAsync: triggerUpdateUnitSize, isPending: loading } =
    useMutation<UnitSizeRead, Error, UpdateArgs>({
      mutationFn: ({ id, payload }) => updateUnitSize(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_SIZES_QK });
      },
    });

  return { triggerUpdateUnitSize, loading };
}

export function useRemoveUnitSizeById() {
  const queryClient = useQueryClient();

  const { mutateAsync: removeUnitSize, isPending: loading } = useMutation<
    UnitSizeRead,
    Error,
    string
  >({
    mutationFn: deleteUnitSize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNIT_SIZES_QK });
    },
    onError: () => {
      console.error("Error deleting the unit size");
    },
  });

  return { removeUnitSize, loading };
}

export function useUploadUnitSizeImage() {
  const queryClient = useQueryClient();

  type UploadArgs = { id: string; file: File };

  const { mutateAsync: triggerUploadImage, isPending: loading } = useMutation<
    UnitSizeRead,
    Error,
    UploadArgs
  >({
    mutationFn: ({ id, file }) => uploadUnitSizeImage(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNIT_SIZES_QK });
    },
  });

  return { triggerUploadImage, loading };
}

export function useAssignCategoryToSizes() {
  const queryClient = useQueryClient();

  const { mutateAsync: triggerAssignCategory, isPending: loading } =
    useMutation<UnitSizeRead[], Error, AssignCategoryPayload>({
      mutationFn: (payload) => assignCategoryToSizes(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_SIZES_QK });
      },
    });

  return { triggerAssignCategory, loading };
}
